import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TitleDivider, useTheme } from '@Sage/hoi-poi-ui';

import { getLiteral } from 'utils/getLiteral';
import DeliveryHealth from './DeliveryHealth';
import FunnelBar from './FunnelBar';
import MetricBox from './MetricBox';

const hasDeliveryHealthData = (health) => {
    if (!health) return false;
    const total = (health.delivered || 0) + (health.bounced || 0);
    return total > 0;
};

const EngagementFunnel = memo(({ funnel, health }) => {
    const theme = useTheme();

    const bars = useMemo(() => {
        if (!funnel) return [];

        const maxValue = funnel.sent || 1;

        return [
            {
                key: 'sent',
                label: getLiteral('label_marketing_campaigns_metrics_sent_plural'),
                value: funnel.sent || 0,
                maxValue,
                color: theme.colors.actionMajor[150],
                textColor: theme.colors.actionMajor[700],
                rateLabel: getLiteral('label_marketing_campaigns_metrics_delivery_rate'),
                rateValue: funnel.deliveryRate,
            },
            {
                key: 'delivered',
                label: getLiteral('label_marketing_campaigns_metrics_delivered_plural'),
                value: funnel.delivered || 0,
                maxValue,
                color: theme.colors.actionMajor[350],
                textColor: theme.colors.actionMajor[700],
                rateLabel: getLiteral('label_marketing_campaigns_metrics_open_rate'),
                rateValue: funnel.openRate,
            },
            {
                key: 'opened',
                label: getLiteral('label_marketing_campaigns_metrics_opened_plural'),
                value: funnel.opened || 0,
                maxValue,
                color: theme.colors.actionMajor[450],
                textColor: theme.colors.textDark.primary,
                rateLabel: getLiteral('label_marketing_campaigns_metrics_ctor'),
                rateValue: funnel.clickToOpenRate,
            },
            {
                key: 'clicked',
                label: getLiteral('label_marketing_campaigns_metrics_clicked_plural'),
                value: funnel.clicked || 0,
                maxValue,
                color: theme.colors.actionMajor[600],
                textColor: theme.colors.textDark.primary,
            },
        ];
    }, [funnel, theme.colors.actionMajor, theme.colors.textDark]);

    const metrics = useMemo(() => {
        if (!funnel) return [];
        return [
            {
                key: 'openRate',
                label: getLiteral('label_marketing_campaigns_metrics_open_rate'),
                value: funnel.openRate || 0,
            },
            {
                key: 'clickRate',
                label: getLiteral('label_marketing_campaigns_metrics_click_rate'),
                value: funnel.clickRate || 0,
            },
            {
                key: 'ctor',
                label: getLiteral('label_marketing_campaigns_metrics_ctor'),
                value: funnel.clickToOpenRate || 0,
            },
        ];
    }, [funnel]);

    if (!funnel) return null;

    const showDeliveryHealth = hasDeliveryHealthData(health);

    return (
        <div className="fm-engagement-funnel">
            <TitleDivider type="subtitle">
                {getLiteral('label_marketing_campaigns_metrics_engagement_funnel')}
            </TitleDivider>
            <div
                className={
                    showDeliveryHealth
                        ? 'fm-engagement-funnel__body'
                        : 'fm-engagement-funnel__body fm-engagement-funnel__body--full'
                }
            >
                <div className="fm-engagement-funnel__main">
                    <div className="fm-engagement-funnel__bars">
                        {bars.map((bar) => (
                            <FunnelBar
                                key={bar.key}
                                label={bar.label}
                                value={bar.value}
                                maxValue={bar.maxValue}
                                color={bar.color}
                                textColor={bar.textColor}
                                rateLabel={bar.rateLabel}
                                rateValue={bar.rateValue}
                            />
                        ))}
                    </div>
                    <div className="fm-funnel-metrics">
                        {metrics.map((metric) => (
                            <MetricBox key={metric.key} label={metric.label} value={metric.value} />
                        ))}
                    </div>
                </div>
                {showDeliveryHealth ? (
                    <div className="fm-engagement-funnel__aside">
                        <DeliveryHealth health={health} />
                    </div>
                ) : null}
            </div>
        </div>
    );
});

EngagementFunnel.displayName = 'EngagementFunnel';

EngagementFunnel.propTypes = {
    health: PropTypes.shape({
        bounced: PropTypes.number,
        delivered: PropTypes.number,
    }),
    funnel: PropTypes.shape({
        sent: PropTypes.number,
        deliveryRate: PropTypes.number,
        delivered: PropTypes.number,
        openRate: PropTypes.number,
        opened: PropTypes.number,
        clickToOpenRate: PropTypes.number,
        clicked: PropTypes.number,
        clickRate: PropTypes.number,
    }),
};

export default EngagementFunnel;
